/** デバイス管理の画面で共通に使う問い合わせと小道具。 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { useCallback } from "react";

import { useApi, useMe } from "../../lib/useApi";
import { hasFeature } from "../../lib/features";
import { hasRole } from "../../lib/roles";

/** 書き込みの可否。ボタンの出し分けに使う(権限の真実は API 側)。 */
export function useMdmPerms() {
  const me = useMe();
  return {
    canView: hasRole(me.data, "mdm:viewer"),
    canWrite: hasRole(me.data, "mdm:operator"),
    canAdmin: hasRole(me.data, "mdm:admin"),
    // 契約に Android が入っているか。台帳は Apple / Windows だけの契約でも開ける
    // ので、取得元が android でもこれが false なら Android タブは出さない
    // (出すと 403 になる問い合わせを 10 秒ごとに投げ続ける)
    hasAndroid: hasFeature(me.data, "mdm.android"),
    // 同じ理由で Apple も見る。Windows だけの契約でも台帳は開けるので、
    // Apple 用の MDM タブを無条件に出すと同じことが起きる
    hasApple: hasFeature(me.data, "mdm.apple"),
    // Windows エージェント(配布・登録コード・端末トークン)の口は mdm.windows の門
    hasWindows: hasFeature(me.data, "mdm.windows"),
  };
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function useDevices(params: {
  q?: string;
  source?: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
}) {
  const api = useApi();
  const search = new URLSearchParams({
    limit: String(params.limit ?? 50),
    offset: String(params.offset ?? 0),
    ...(params.q ? { q: params.q } : {}),
    ...(params.source ? { source: params.source } : {}),
  });
  return useQuery({
    queryKey: ["mdm", "devices", params],
    queryFn: () => api.devices(search),
    enabled: params.enabled ?? true,
  });
}

export function useDevice(id: number | null) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "device", id],
    queryFn: () => api.device(id!),
    enabled: !!id,
  });
}

/** レジストリ配布の対象に選べる動的グループ(AG2)。 */
export function useDynGroups(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "registry-groups"],
    queryFn: () => api.registryGroups(),
    enabled,
  });
}

export function useRegistryPolicies(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "registry-policies"],
    queryFn: api.registryPolicies,
    enabled,
  });
}

/** `agent`(トークン)と `mdm`(Apple MDM)。読めるのは mdm:admin だけ。 */
export function useMdmSetting(key: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "setting", key],
    queryFn: () => api.mdmSetting(key),
    enabled,
  });
}

/** 台帳・設定が動いたら mdm と監査のキャッシュをまとめて捨てる。 */
export function useInvalidateMdm() {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["mdm"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  }, [qc]);
}

/** 登録プロファイルを落とす。Authorization が要るので fetch して blob にする。 */
export function useDownload() {
  const api = useApi();
  return useCallback(
    async (path: string) => {
      try {
        const { blob, filename } = await api.download(path);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        void message.error(errorText(e));
      }
    },
    [api],
  );
}

/**
 * Apple MDM のコマンド履歴。**Drawer を開けている間だけ** 5 秒ごとに見に行く
 * (端末の応答は Webhook で後から届くので、押した直後は queued / pushed のまま)。
 */
export function useDeviceCommands(id: number | null, polling: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "device-commands", id],
    queryFn: () => api.deviceCommands(id!, new URLSearchParams({ limit: "50" })),
    enabled: !!id,
    refetchInterval: polling ? 5000 : false,
  });
}

/**
 * Webhook の受け口の件数。**無視された件数が見えないと、台帳に出ない原因が分からない**
 * (2026-09-28 の実機登録でそれが起きた)ので 30 秒ごとに見に行く。
 */
export function useMdmWebhookStatus(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "webhook-status"],
    queryFn: api.mdmWebhookStatus,
    enabled,
    refetchInterval: enabled ? 30_000 : false,
  });
}

/** MDM に登録されている端末の一覧(台帳とは別)。 */
export function useMdmEnrollments(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "enrollments"],
    queryFn: () => api.mdmEnrollments(new URLSearchParams({ limit: "200" })),
    enabled,
  });
}

// ---- Android MDM(MOB2)-------------------------------------------------------
//
// 機能キー `mdm.android` がライセンスに無ければ、ナビにも出ないし API も 403。
// 画面はそれを前提に、エラーをそのまま出す(fail closed)。

/** 設定(`mdm:admin` だけ読める)。SA の鍵は伏字で返る。 */
export function useAndroidConfig(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "android", "config"],
    queryFn: api.androidConfig,
    enabled,
  });
}

/** 配ったポリシーの控えとテンプレ 3 つ。 */
export function useAndroidPolicies(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "android", "policies"],
    queryFn: api.androidPolicies,
    enabled,
  });
}

/** 登録トークンの控え。**値も QR も入らない**(保存していない)。 */
export function useAndroidTokens(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "android", "tokens"],
    queryFn: () => api.androidTokens(new URLSearchParams({ limit: "50" })),
    enabled,
  });
}

/**
 * Android のコマンド履歴。**Drawer を開けている間だけ** 10 秒ごとに見に行く。
 * 決着が付くのは 5 分ごとの同期なので、Apple(5 秒)より緩くする。
 */
export function useAndroidDeviceCommands(id: number | null, polling: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: ["mdm", "android", "device-commands", id],
    queryFn: () => api.androidDeviceCommands(id!, new URLSearchParams({ limit: "50" })),
    enabled: !!id,
    refetchInterval: polling ? 10_000 : false,
  });
}
